import type { Border, Decoration } from '../storage/schemas';

// Original decoration artwork, as SVG in a 1000-unit-wide coordinate system so the same markup renders in the
// DOM (as an <img>) and in canvas exports.

const star = (x: number, y: number, r: number, fill: string) => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 ? r * 0.45 : r;
    pts.push(`${(x + Math.cos(a) * rr).toFixed(1)},${(y + Math.sin(a) * rr).toFixed(1)}`);
  }
  return `<polygon points="${pts.join(' ')}" fill="${fill}"/>`;
};
const heart = (x: number, y: number, s: number, fill: string) =>
  `<path transform="translate(${x} ${y}) scale(${s})" d="M0 6 C-10 -4 -22 6 0 22 C22 6 10 -4 0 6Z" fill="${fill}"/>`;
const leaf = (x: number, y: number, s: number, rot: number, fill: string) =>
  `<path transform="translate(${x} ${y}) rotate(${rot}) scale(${s})" d="M0 0 C14 -14 34 -12 40 0 C34 12 14 14 0 0Z M2 0 L36 0" fill="${fill}" stroke="${fill}" stroke-width="1.5"/>`;
const flake = (x: number, y: number, r: number, c: string) =>
  `<g stroke="${c}" stroke-width="${(r / 6).toFixed(1)}" stroke-linecap="round">${[0, 60, 120]
    .map((a) => {
      const dx = Math.cos((a * Math.PI) / 180) * r;
      const dy = Math.sin((a * Math.PI) / 180) * r;
      return `<line x1="${x - dx}" y1="${y - dy}" x2="${x + dx}" y2="${y + dy}"/>`;
    })
    .join('')}</g>`;
const sparkle = (x: number, y: number, r: number, c: string) =>
  `<path d="M${x} ${y - r} Q${x + r * 0.15} ${y - r * 0.15} ${x + r} ${y} Q${x + r * 0.15} ${y + r * 0.15} ${x} ${y + r} Q${x - r * 0.15} ${y + r * 0.15} ${x - r} ${y} Q${x - r * 0.15} ${y - r * 0.15} ${x} ${y - r}Z" fill="${c}"/>`;

export function decorationSvg(dec: Decoration | undefined, accent: string, line: string, aspect: number): string | null {
  if (!dec || dec === 'none') return null;
  const W = 1000;
  const H = Math.round(W / aspect);
  let body = '';
  switch (dec) {
    case 'tape':
      body = `<rect x="${W / 2 - 110}" y="-18" width="220" height="62" rx="6" transform="rotate(-3 ${W / 2} 12)" fill="${accent}" fill-opacity="0.28"/>
      <rect x="${W / 2 - 110}" y="-18" width="220" height="62" rx="6" transform="rotate(-3 ${W / 2} 12)" fill="none" stroke="${accent}" stroke-opacity="0.18" stroke-dasharray="6 5"/>`;
      break;
    case 'pin':
      body = `<circle cx="${W / 2 + 4}" cy="34" r="22" fill="#000" fill-opacity="0.15"/><circle cx="${W / 2}" cy="28" r="22" fill="${accent}"/><circle cx="${W / 2 - 7}" cy="21" r="7" fill="#fff" fill-opacity="0.55"/>`;
      break;
    case 'corner':
      body = `<path d="M${W - 90} 0 L${W} 0 L${W} 90 Z" fill="${line}"/><path d="M${W - 90} 0 L${W - 90} 90 L${W} 90 Z" fill="#000" fill-opacity="0.08"/>`;
      break;
    case 'stars':
      body = [star(60, 60, 26, accent), star(940, 70, 18, accent), star(900, H - 120, 14, accent), star(110, H - 70, 16, accent), star(820, 40, 10, accent)].join('');
      break;
    case 'hearts':
      body = [heart(70, 40, 2.2, accent), heart(930, 50, 1.6, accent), heart(120, H - 90, 1.4, accent), heart(880, H - 140, 1.2, accent)].join('');
      break;
    case 'leaves':
      body = [leaf(30, 50, 2.2, 30, accent), leaf(950, 40, 1.8, 140, accent), leaf(40, H - 60, 1.6, -30, accent), leaf(960, H - 150, 1.4, 200, accent)].join('');
      break;
    case 'snow':
      body = [flake(70, 70, 24, accent), flake(930, 60, 18, accent), flake(860, 150, 10, accent), flake(110, H - 90, 14, accent), flake(150, 140, 9, accent)].join('');
      break;
    case 'sun':
      body = `<circle cx="70" cy="70" r="34" fill="${accent}"/>${Array.from({ length: 8 }, (_, i) => {
        const a = (i * Math.PI) / 4;
        return `<line x1="${70 + Math.cos(a) * 46}" y1="${70 + Math.sin(a) * 46}" x2="${70 + Math.cos(a) * 66}" y2="${70 + Math.sin(a) * 66}" stroke="${accent}" stroke-width="8" stroke-linecap="round"/>`;
      }).join('')}`;
      break;
    case 'sparkle':
      body = [sparkle(70, 70, 30, accent), sparkle(930, 80, 22, accent), sparkle(890, H - 110, 16, accent), sparkle(120, H - 80, 14, accent)].join('');
      break;
    case 'margin':
      body = `<line x1="88" y1="0" x2="88" y2="${H}" stroke="#E4572E" stroke-opacity="0.55" stroke-width="3"/><line x1="96" y1="0" x2="96" y2="${H}" stroke="#E4572E" stroke-opacity="0.35" stroke-width="2"/>`;
      break;
    case 'binder':
      body = [0.2, 0.5, 0.8].map((f) => `<circle cx="32" cy="${Math.round(H * f)}" r="15" fill="#000" fill-opacity="0.18"/>`).join('');
      break;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">${body}</svg>`;
}

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** CSS for the inner border overlay (width in cqw so it scales with the card). */
export function borderCss(b: Border | undefined): Record<string, string> | null {
  if (!b || b.style === 'none' || b.width <= 0) return null;
  const w = `${(b.width * 100).toFixed(2)}cqw`;
  switch (b.style) {
    case 'solid':
      return { boxShadow: `inset 0 0 0 ${w} ${b.color}` };
    case 'double':
      return { border: `${w} double ${b.color}` };
    case 'dashed':
      return { border: `${w} dashed ${b.color}` };
    case 'stitch':
      return { border: `${w} dashed ${b.color}`, inset: '2.2cqw', borderRadius: '3.5cqw' };
  }
}
